/**
 * 템플릿으로 등록(2026-10-30) — 잘 만든 화면(올린 HTML·템플릿)을 [콘텐츠 추가] ▸ [템플릿 만들기] 의 "등록한 템플릿" 에 둔다.
 * 고르면 이 콘텐츠의 사본에서 새 콘텐츠를 만든다(이 콘텐츠는 그대로). 이름·설명은 고르기 목록의 카드에 그대로 나온다.
 */
import { useState, type ReactNode } from 'react';
import type { Asset, SignageOneClient } from '../api/client.js';
import { ErrorNote, Modal } from '../components/ui.js';
import { notify } from '../components/Toast.js';

export function TemplateEntryModal({
  client,
  asset,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  asset: Asset;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const entry = asset.template_entry ?? null;
  const [name, setName] = useState(entry?.name ?? asset.title);
  const [description, setDescription] = useState(entry?.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal title={entry ? '템플릿 이름·설명' : '템플릿으로 등록'} onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) {
            setError('템플릿 이름을 입력하세요');
            return;
          }
          setSaving(true);
          setError(null);
          client.registerTemplate(asset.id, { name: trimmed, description: description.trim() }).then(
            () => {
              notify(entry ? `템플릿 「${trimmed}」 을(를) 고쳤습니다` : `「${trimmed}」 을(를) 템플릿으로 등록했습니다`);
              onSaved();
            },
            (cause: unknown) => {
              setSaving(false);
              setError(cause instanceof Error ? cause.message : '등록하지 못했습니다');
            },
          );
        }}
      >
        <p className="text-[13px] leading-relaxed text-slate-600">
          [콘텐츠 추가] ▸ [템플릿 만들기] 에 이 화면이 나옵니다. 고르면 이 화면의 사본을{' '}
          {asset.type === 'template' ? '[내용 편집]' : '[화면 편집]'} 으로 열어 새 콘텐츠를 만듭니다 — 이 콘텐츠는 그대로
          남습니다.
        </p>
        {error ? <ErrorNote message={error} onDismiss={() => setError(null)} /> : null}
        <label className="field">
          <span>템플릿 이름</span>
          <input
            className="input"
            value={name}
            maxLength={60}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span>설명(선택)</span>
          <input
            className="input"
            value={description}
            maxLength={200}
            placeholder="예: 공장마다 시작일만 바꿔 씁니다"
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {entry ? '저장' : '등록'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
